import axios from 'axios';

// Normalize backend URL - remove trailing slash
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Always use /api prefix - the backend expects routes at /api/*
// For local dev with proxy, this routes through the proxy
// For production/preview, this appends /api to the backend URL
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Export for components using direct fetch
export const getApiUrl = () => BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Log API configuration
console.log('[API] Backend URL:', BACKEND_URL || '(using /api proxy)');
console.log('[API] API Base:', API_BASE);

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Don't redirect if already on auth page
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Product APIs
export const productAPI = {
  getAll: async (includeInactive = false, region = null) => {
    const params = { include_inactive: includeInactive };
    // Get region from localStorage if not provided
    if (!region) {
      try {
        const savedRegion = localStorage.getItem('amidex_region');
        if (savedRegion) {
          region = savedRegion;
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    if (region) {
      params.region = region;
    }
    try {
      const response = await api.get('/products', { params });
      return response;
    } catch (error) {
      console.error('[API] Failed to fetch products:', error.message, error.response?.status);
      throw error;
    }
  },
  getOne: async (id, region = null) => {
    const params = {};
    if (!region) {
      try {
        const savedRegion = localStorage.getItem('amidex_region');
        if (savedRegion) {
          region = savedRegion;
        }
      } catch (e) {}
    }
    if (region) {
      params.region = region;
    }
    try {
      const response = await api.get(`/products/${id}`, { params });
      return response;
    } catch (error) {
      console.error(`[API] Failed to fetch product ${id}:`, error.message);
      throw error;
    }
  },
  getBySlug: async (slug, region = null) => {
    const params = {};
    if (!region) {
      try {
        const savedRegion = localStorage.getItem('amidex_region');
        if (savedRegion) {
          region = savedRegion;
        }
      } catch (e) {}
    }
    if (region) {
      params.region = region;
    }
    try {
      const response = await api.get(`/products/by-slug/${slug}`, { params });
      return response;
    } catch (error) {
      console.error(`[API] Failed to fetch product by slug ${slug}:`, error.message);
      throw error;
    }
  },
  getSlugs: () => api.get('/seo/product-slugs'),
  create: (data) => api.post('/admin/products', data),
  update: (id, data) => api.put(`/admin/products/${id}`, data),
  delete: (id) => api.delete(`/admin/products/${id}`),
  uploadCOA: (id, data) => api.post(`/admin/products/${id}/coa`, data),
};

// Cart APIs
export const cartAPI = {
  get: () => api.get('/cart'),
  add: (productId, quantity) => api.post('/cart/add', { product_id: productId, quantity }),
  update: (productId, quantity) => api.put('/cart/update', { product_id: productId, quantity }),
  remove: (productId) => api.delete(`/cart/item/${productId}`),
  clear: () => api.delete('/cart'),
  merge: (items) => api.post('/cart/merge', { items }),
};

// Order APIs
export const orderAPI = {
  create: (data) => api.post('/orders', data),
  getAll: () => api.get('/orders'),
  getOne: (id) => api.get(`/orders/${id}`),
};

// User Profile APIs
export const userAPI = {
  // Profile
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/user/profile', data),
  changePassword: (data) => api.put('/user/password', data),
  
  // Dashboard
  getDashboard: () => api.get('/user/dashboard'),
  
  // Addresses
  getAddresses: () => api.get('/user/addresses'),
  createAddress: (data) => api.post('/user/addresses', data),
  updateAddress: (id, data) => api.put(`/user/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/user/addresses/${id}`),
  setDefaultAddress: (id, type) => api.put(`/user/addresses/${id}/default`, null, { params: { address_type: type } }),
  getSavedAddress: () => api.get('/user/saved-address'),
  
  // Wishlist
  getWishlist: () => api.get('/user/wishlist'),
  addToWishlist: (productId) => api.post(`/user/wishlist/${productId}`),
  removeFromWishlist: (productId) => api.delete(`/user/wishlist/${productId}`),
  checkWishlist: (productId) => api.get(`/user/wishlist/check/${productId}`),
};

// Admin Order APIs
export const adminOrderAPI = {
  getAll: (status) => api.get('/admin/orders', { params: status ? { status } : {} }),
  update: (id, data) => api.put(`/admin/orders/${id}`, data),
};

// Admin Customer APIs
export const adminCustomerAPI = {
  getAll: () => api.get('/admin/customers'),
  getOne: (id) => api.get(`/admin/customers/${id}`),
};

// Promo APIs
export const promoAPI = {
  validate: (code) => api.post('/promos/validate', null, { params: { code } }),
  getAll: () => api.get('/admin/promos'),
  create: (data) => api.post('/admin/promos', data),
  update: (id, data) => api.put(`/admin/promos/${id}`, data),
  delete: (id) => api.delete(`/admin/promos/${id}`),
};

// COA APIs
export const coaAPI = {
  getAll: (params) => api.get('/coas', { params }),
  getByProduct: (productId) => api.get(`/coas/product/${productId}`),
  getOne: (id) => api.get(`/coas/${id}`),
  create: (data) => api.post('/admin/coas', data),
  update: (id, data) => api.put(`/admin/coas/${id}`, data),
  delete: (id) => api.delete(`/admin/coas/${id}`),
  getAdmin: () => api.get('/admin/coas'),
};

// Research Study APIs
export const researchStudyAPI = {
  getStudies: (peptideSlug, params) => api.get(`/research/studies/${peptideSlug}`, { params }),
  getStudy: (peptideSlug, studyId) => api.get(`/research/studies/${peptideSlug}/${studyId}`),
  getFilters: (peptideSlug) => api.get(`/research/studies/${peptideSlug}/filters`),
  // Admin
  create: (data) => api.post('/admin/research/studies', data),
  update: (studyId, data) => api.put(`/admin/research/studies/${studyId}`, data),
  delete: (studyId) => api.delete(`/admin/research/studies/${studyId}`),
  getAll: (params) => api.get('/admin/research/studies', { params }),
  seedBPC157: () => api.post('/seed-bpc157-studies'),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', data),
  getPublic: () => api.get('/settings/public'),
};

// Payment APIs
export const paymentAPI = {
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Tax APIs
export const taxAPI = {
  calculate: (data) => api.post('/tax/calculate', data),
  getStates: () => api.get('/tax/states'),
};

// Admin Stats API
export const statsAPI = {
  get: () => api.get('/admin/stats'),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Research Portal APIs
export const portalAPI = {
  // Peptide profiles
  getPeptides: (params) => api.get('/portal/peptides', { params }),
  getPeptide: (slug) => api.get(`/portal/peptides/${slug}`),
  getCategories: () => api.get('/portal/categories'),
  
  // Research updates
  getUpdates: (params) => api.get('/portal/updates', { params }),
  getUpdate: (id) => api.get(`/portal/updates/${id}`),
  
  // Bookmarks (authenticated)
  getBookmarks: () => api.get('/portal/bookmarks'),
  createBookmark: (contentType, contentId) => api.post('/portal/bookmarks', { content_type: contentType, content_id: contentId }),
  deleteBookmark: (id) => api.delete(`/portal/bookmarks/${id}`),
  deleteBookmarkByContent: (contentType, contentId) => api.delete(`/portal/bookmarks/content/${contentType}/${contentId}`),
  
  // Seed portal data
  seedPortal: () => api.post('/portal/seed'),
};

// Admin Portal APIs
export const adminPortalAPI = {
  // Peptide management
  getPeptides: () => api.get('/admin/portal/peptides'),
  createPeptide: (data) => api.post('/admin/portal/peptides', data),
  updatePeptide: (id, data) => api.put(`/admin/portal/peptides/${id}`, data),
  deletePeptide: (id) => api.delete(`/admin/portal/peptides/${id}`),
  
  // Study management
  createStudy: (data) => api.post('/admin/portal/studies', data),
  updateStudy: (id, data) => api.put(`/admin/portal/studies/${id}`, data),
  deleteStudy: (id) => api.delete(`/admin/portal/studies/${id}`),
  
  // Research updates management
  getUpdates: () => api.get('/admin/portal/updates'),
  createUpdate: (data) => api.post('/admin/portal/updates', data),
  updateUpdate: (id, data) => api.put(`/admin/portal/updates/${id}`, data),
  deleteUpdate: (id) => api.delete(`/admin/portal/updates/${id}`),
  
  // Stats
  getStats: () => api.get('/admin/portal/stats'),
};

export default api;
